import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  operatorsAdminSlackPrivateChannelsQueryOptions,
  updateAdminSlackPrivateChannelsMutationOptions,
} from "../../lib/operatorsAdminReactQuery";

import { SettingsInfoTip } from "./SettingsInfoTip";
import { SettingsSection } from "./SettingsChrome";

export function SlackPrivateChannelsSection() {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");

  const channelsQuery = useQuery(operatorsAdminSlackPrivateChannelsQueryOptions());
  const updateChannels = useMutation(
    updateAdminSlackPrivateChannelsMutationOptions({ queryClient }),
  );

  // Never write from a placeholder: the loaded list is the only safe base to edit against.
  const loadedChannels = channelsQuery.data?.channels ?? null;
  const channels = loadedChannels ?? [];
  // React Query keeps the last good `data` when a refetch fails, so a stale snapshot
  // must not be edited either.
  const canEdit = loadedChannels !== null && !channelsQuery.isError && !updateChannels.isPending;
  const save = async (next: string[]) => {
    if (loadedChannels === null || channelsQuery.isError) return;
    await updateChannels.mutateAsync({ channels: next });
  };

  return (
    <SettingsSection
      title="Slack private channels"
      description="Operator answers @-mentions in every public channel. Private channels must be listed here, and the bot has to be invited to each one."
      actions={
        <SettingsInfoTip label="Add a channel ID (C0123ABCD) or a name (#ops-private). IDs are safer: renaming a channel silently changes name-based access. Changes apply within 30 seconds." />
      }
    >
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={async (event) => {
          event.preventDefault();
          const entry = draft.trim();
          if (!entry) return;
          setDraft("");
          await save([...channels, entry]);
        }}
      >
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="C0123ABCD or #ops-private"
          disabled={!canEdit}
          aria-label="Slack channel ID or name"
          className="h-9 w-full max-w-xs rounded-lg border border-border bg-background px-3 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        <button
          type="submit"
          disabled={!canEdit || draft.trim().length === 0}
          className="h-9 rounded-lg border border-border px-3 text-sm font-medium text-foreground hover:bg-muted/40 disabled:opacity-50"
        >
          Add
        </button>
      </form>

      {channelsQuery.isLoading ? (
        <p className="mt-4 text-sm text-muted-foreground">Loading channels…</p>
      ) : channelsQuery.isError ? (
        <p className="mt-4 text-sm text-destructive">
          {channelsQuery.error instanceof Error
            ? channelsQuery.error.message
            : "Failed to load channels"}
        </p>
      ) : channels.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">
          No private channels yet — Operator stays silent in all of them.
        </p>
      ) : (
        <ul className="mt-4 flex flex-wrap gap-2">
          {channels.map((channel) => (
            <li
              key={channel}
              className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1 text-sm text-foreground"
            >
              {channel}
              <button
                type="button"
                aria-label={`Remove ${channel}`}
                disabled={!canEdit}
                onClick={async () => {
                  await save(channels.filter((entry) => entry !== channel));
                }}
                className="text-muted-foreground hover:text-destructive disabled:opacity-50"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      {updateChannels.isError ? (
        <p className="mt-3 text-sm text-destructive">
          {updateChannels.error instanceof Error
            ? updateChannels.error.message
            : "Could not save channels"}
        </p>
      ) : null}
    </SettingsSection>
  );
}
